/**
 * Preset counterparty for a NEW document (a business's or person's Add menu).
 * Shared by `CreateExpenseForm`, `CreateAPInvoiceForm` and
 * `CreateARInvoiceForm` — same rule as their `defaultProjectId`: seeds the
 * create-mode `defaultValues` only, ignored in edit mode, and any source that
 * carries its own counterparty overrides it.
 */
export interface DefaultCounterparty {
  type: 'business' | 'person';
  id: string;
}

/**
 * The counterparty id fields a preset seeds. A linked business binds its
 * SearchableSelect through `business_name` (options are keyed by id), so the
 * id goes in both; the other side stays empty. `null` = no preset applies
 * (absent, or edit mode) — the form keeps its own defaults.
 */
export function counterpartyPresetFields(
  preset: DefaultCounterparty | undefined,
  isEditMode: boolean,
): { type: 'business' | 'person'; business_id: string; business_name: string; person_id: string } | null {
  if (!preset || isEditMode || !preset.id) return null;
  return preset.type === 'business'
    ? { type: 'business', business_id: preset.id, business_name: preset.id, person_id: '' }
    : { type: 'person', business_id: '', business_name: '', person_id: preset.id };
}

/**
 * The create-form presets for a list or overview narrowed to one record — the
 * one rule behind every scoped "New expense / bill / invoice": a project scope
 * presets the project, a business or person scope the counterparty. Takes the
 * scope filter every scoped surface already has (`{ projectId } | { businessId
 * } | { personId }`). Spread onto `CreateExpenseForm` / `CreateAPInvoiceForm`
 * / `CreateARInvoiceForm`; each form applies it on create only.
 */
export function documentPresetsFor(filter: {
  projectId?: string;
  businessId?: string;
  personId?: string;
} | undefined): { defaultProjectId?: string; defaultCounterparty?: DefaultCounterparty } {
  if (!filter) return {};
  return {
    defaultProjectId: filter.projectId || undefined,
    defaultCounterparty: filter.businessId
      ? { type: 'business', id: filter.businessId }
      : filter.personId
        ? { type: 'person', id: filter.personId }
        : undefined,
  };
}
